<template>
  <div :class="['passport-view', { 'registry-passport': nauchete }]">
    <!-- Загрузка -->
    <div v-if="loading" class="text-center">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
      <p class="mt-3">Поиск паспорта...</p>
    </div>

    <!-- Номер введён с ошибкой (контрольный символ не сошёлся) -->
    <div v-else-if="invalidCode" class="alert alert-warning text-center">
      <h4>Номер введён с ошибкой</h4>
      <p class="mb-3">
        Идентификатор <strong>{{ $route.params.identifier }}</strong> не проходит проверку.
        Сверьте номер с этикеткой — скорее всего, один знак прочитан неверно.
      </p>
      <router-link to="/" class="btn btn-primary">Вернуться на главную</router-link>
    </div>

    <!-- Номер можно прочитать по-разному: показываем варианты -->
    <div v-else-if="ambiguous.length" class="alert alert-warning">
      <h4 class="text-center">Такой номер можно прочитать по-разному</h4>
      <p class="text-center mb-3">
        Под <strong>{{ $route.params.identifier }}</strong> подходит несколько паспортов.
        Выберите нужный по номеру поставщика.
      </p>
      <div class="list-group">
        <router-link
          v-for="item in ambiguous"
          :key="item.identifier"
          :to="`/${item.identifier}`"
          class="list-group-item list-group-item-action d-flex justify-content-between"
        >
          <span class="font-monospace fw-bold">{{ item.identifier }}</span>
          <span class="text-muted">серийный № {{ item.serial_number || '—' }}</span>
        </router-link>
      </div>
    </div>

    <!-- Паспорт не найден -->
    <div v-else-if="notFound" class="alert alert-warning text-center">
      <i class="bi bi-exclamation-triangle display-1 mb-3"></i>
      <h4>Паспорт не найден</h4>
      <p class="mb-3">
        Паспорт с идентификатором <strong>{{ $route.params.identifier }}</strong> не найден в базе данных.
      </p>
      <router-link to="/" class="btn btn-primary">
        Вернуться на главную
      </router-link>
    </div>

    <!-- Ошибка -->
    <div v-else-if="error" class="alert alert-danger text-center">
      <i class="bi bi-x-circle display-1 mb-3"></i>
      <h4>Ошибка</h4>
      <p class="mb-3">{{ error }}</p>
      <button @click="loadPassport" class="btn btn-primary me-2">
        Попробовать снова
      </button>
      <router-link to="/" class="btn btn-secondary">
        Вернуться на главную
      </router-link>
    </div>

    <!-- Паспорт найден -->
    <div v-else-if="passport" class="passport-container">
      <div class="row" v-if="lookalikes.length">
        <div class="col-lg-8 mx-auto">
          <div class="alert alert-warning">
            <strong>Внимание: у этого номера есть похожие.</strong>
            Идентификатор старого формата, в нём легко спутать <code>0</code> с <code>O</code> и <code>1</code> с <code>I</code>.
            Сверьте серийный номер <strong>{{ passport.serial_number || '—' }}</strong>, прежде чем работать с паспортом.
            <div class="mt-2">
              Похожие:
              <router-link
                v-for="item in lookalikes"
                :key="item.identifier"
                :to="`/${item.identifier}`"
                class="badge bg-secondary text-decoration-none me-1"
              >{{ item.identifier }} — серийный № {{ item.serial_number || '—' }}</router-link>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-8 mx-auto">
          <div class="card shadow-lg">
            <div class="card-header bg-primary text-white">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">
                    <i class="bi bi-file-text"></i>
                    Паспорт газового баллона
                  </h3>
                </div>
                <div class="col-auto" v-if="isAdmin">
                  <button 
                    @click="editMode = !editMode" 
                    class="btn btn-light btn-sm"
                    :class="{ 'active': editMode }"
                  >
                    <i class="bi bi-pencil"></i>
                    {{ editMode ? 'Отмена' : 'Редактировать' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="card-body p-4">
              <!-- Идентификатор -->
              <div class="row mb-4">
                <div class="col-sm-3">
                  <strong>Идентификатор:</strong>
                </div>
                <div class="col-sm-9">
                  <span class="badge bg-primary fs-6">{{ passport.identifier }}</span>
                  <button
                    v-if="isAdmin && !reassigning"
                    @click="reassignOpen = !reassignOpen"
                    class="btn btn-outline-secondary btn-sm ms-2"
                    type="button"
                  >
                    {{ reassignOpen ? 'Отмена' : 'Сменить' }}
                  </button>

                  <div v-if="movedFrom" class="form-text">
                    Прежний номер <span class="font-monospace">{{ movedFrom }}</span> тоже открывает этот паспорт.
                  </div>

                  <div v-if="isAdmin && reassignOpen" class="border rounded p-3 mt-2 bg-light">
                    <p class="small text-muted mb-2">
                      Ёмкость переезжает в другую серию: номер выдаётся следующий свободный
                      в выбранном префиксе. Старый идентификатор продолжит открывать этот паспорт,
                      так что уже наклеенный QR не пропадёт.
                    </p>

                    <div class="row g-2 align-items-end">
                      <div class="col-sm-5">
                        <label class="form-label mb-1"><strong>Новый префикс:</strong></label>
                        <input
                          type="text"
                          class="form-control"
                          v-model="reassignPrefix"
                          list="known-prefixes"
                          placeholder="MNB"
                          maxlength="6"
                          :disabled="!!reassignIdentifier"
                        >
                        <datalist id="known-prefixes">
                          <option v-for="p in knownPrefixes" :key="p" :value="p"></option>
                        </datalist>
                      </div>
                      <div class="col-sm-5">
                        <label class="form-label mb-1">Или идентификатор целиком:</label>
                        <input
                          type="text"
                          class="form-control font-monospace"
                          v-model="reassignIdentifier"
                          placeholder="MNB-31N0T"
                          maxlength="20"
                        >
                      </div>
                      <div class="col-sm-2">
                        <button
                          @click="reassignIdentifierAction"
                          class="btn btn-primary w-100"
                          type="button"
                          :disabled="reassigning || (!reassignPrefix.trim() && !reassignIdentifier.trim())"
                        >
                          {{ reassigning ? '…' : 'Сменить' }}
                        </button>
                      </div>
                    </div>

                    <div v-if="reassignError" class="alert alert-danger mt-2 mb-0 py-2">
                      {{ reassignError }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Партия: сертификат и описание, общие для всей поставки -->
              <div class="row mb-4" v-if="batch || isAdmin">
                <div class="col-sm-3">
                  <strong>Партия:</strong>
                </div>
                <div class="col-sm-9">
                  <template v-if="batch">
                    <div class="fw-bold font-monospace">{{ batch.code }}</div>
                    <div v-if="batch.title" class="text-muted">{{ batch.title }}</div>
                    <div class="d-flex flex-wrap gap-2 mt-2">
                      <a
                        v-if="batch.certificate_file"
                        :href="`/uploads/${batch.certificate_file}`"
                        target="_blank"
                        class="btn btn-outline-primary btn-sm"
                      >
                        Сертификат партии
                      </a>
                      <button
                        v-if="batch.description_html"
                        class="btn btn-outline-secondary btn-sm"
                        type="button"
                        @click="descriptionOpen = !descriptionOpen"
                      >
                        {{ descriptionOpen ? 'Скрыть данные сертификата' : 'Данные сертификата' }}
                      </button>
                    </div>
                  </template>
                  <span v-else class="text-muted">не указана</span>

                  <div v-if="isAdmin" class="mt-2">
                    <div class="input-group input-group-sm" style="max-width: 420px">
                      <select class="form-select" v-model="selectedBatchId">
                        <option :value="null">— без партии —</option>
                        <option v-for="item in batches" :key="item.id" :value="item.id">
                          {{ item.code }}{{ item.title ? ' — ' + item.title : '' }}
                        </option>
                      </select>
                      <button class="btn btn-outline-primary" @click="saveBatch" :disabled="savingBatch">
                        {{ savingBatch ? '…' : 'Привязать' }}
                      </button>
                    </div>
                    <div class="form-text">
                      Партии заводятся в разделе <router-link to="/batches">Партии</router-link>.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Описание партии во всю ширину: в нём таблицы из сертификата,
                   в узкой колонке они не помещаются и распирают страницу -->
              <div class="row mb-4" v-if="batch && batch.description_html && descriptionOpen">
                <div class="col-12 batch-description-col">
                  <div class="batch-description border rounded p-3" v-html="batch.description_html"></div>
                </div>
              </div>

              <!-- Форма редактирования или просмотра -->
              <form @submit.prevent="savePassport" v-if="editMode">
                <div class="row mb-3">
                  <label class="col-sm-3 col-form-label">Серийный номер:</label>
                  <div class="col-sm-9">
                    <input
                      type="text"
                      class="form-control"
                      v-model="editData.serial_number"
                      placeholder="Введите серийный номер"
                    >
                  </div>
                </div>

                <div class="row mb-3">
                  <label class="col-sm-3 col-form-label">Партия:</label>
                  <div class="col-sm-9">
                    <input
                      type="text"
                      class="form-control"
                      v-model="editData.batch"
                      placeholder="Напр. RONNIE-2026-05"
                    >
                  </div>
                </div>

                <div class="row mb-3">
                  <label class="col-sm-3 col-form-label">Тип емкости:</label>
                  <div class="col-sm-9">
                    <select 
                      class="form-control" 
                      v-model="editData.container_type"
                    >
                      <option :value="null">Не выбран</option>
                      <option value="КЦ">Криоцилиндр (КЦ)</option>
                      <option value="Баллон">Баллон</option>
                      <option value="Моноблок">Моноблок</option>
                    </select>
                  </div>
                </div>

                <div class="row mb-3">
                  <label class="col-sm-3 col-form-label">Объем (л):</label>
                  <div class="col-sm-9">
                    <input 
                      type="number" 
                      step="0.01"
                      class="form-control" 
                      v-model="editData.volume"
                      placeholder="Введите объем"
                    >
                  </div>
                </div>

                <div class="row mb-3">
                  <label class="col-sm-3 col-form-label">Давление (атм):</label>
                  <div class="col-sm-9">
                    <input 
                      type="number" 
                      step="0.01"
                      class="form-control" 
                      v-model="editData.pressure"
                      placeholder="Введите давление"
                    >
                  </div>
                </div>

                <div class="row mb-3" v-if="editData.container_type === 'Моноблок'">
                  <label class="col-sm-3 col-form-label">Баллонов в связке:</label>
                  <div class="col-sm-9">
                    <input
                      type="number"
                      class="form-control"
                      v-model.number="editData.bundle_size"
                      min="1"
                      step="1"
                      placeholder="12"
                    >
                  </div>
                </div>

                <div class="row mb-3" v-if="editData.container_type === 'КЦ'">
                  <label class="col-sm-3 col-form-label">Наличие рамы:</label>
                  <div class="col-sm-9">
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="edit_has_frame"
                        v-model="editData.has_frame"
                      >
                      <label class="form-check-label" for="edit_has_frame">
                        Есть рама
                      </label>
                    </div>
                  </div>
                </div>

                <div class="row mb-3" v-if="editData.container_type === 'КЦ'">
                  <label class="col-sm-3 col-form-label">Наличие навесного испарителя:</label>
                  <div class="col-sm-9">
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="edit_has_evaporator"
                        v-model="editData.has_evaporator"
                      >
                      <label class="form-check-label" for="edit_has_evaporator">
                        Есть навесной испаритель
                      </label>
                    </div>
                  </div>
                </div>

                <div class="row mb-3">
                  <label class="col-sm-3 col-form-label">Производитель:</label>
                  <div class="col-sm-9">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="editData.manufacturer"
                      placeholder="Введите производителя"
                    >
                  </div>
                </div>

                <div class="row mb-3">
                  <label class="col-sm-3 col-form-label">Газ:</label>
                  <div class="col-sm-9">
                    <select 
                      class="form-control" 
                      v-model="editData.gas"
                    >
                      <option :value="null">Не выбран</option>
                      <option value="Ar">Аргон (Ar)</option>
                      <option value="O2">Кислород (O2)</option>
                      <option value="N2">Азот (N2)</option>
                      <option value="CO2">Углекислота (CO2)</option>
                      <option value="He">Гелий (He)</option>
                      <option value="C3H8">Пропан (C3H8)</option>
                      <option value="LPG">Пропан-бутан (LPG)</option>
                    </select>
                  </div>
                </div>

                <div class="row mb-3">
                  <label class="col-sm-3 col-form-label">Описание:</label>
                  <div class="col-sm-9">
                    <textarea
                      class="form-control"
                      rows="3"
                      v-model="editData.description"
                      placeholder="Дополнительное описание"
                    ></textarea>
                  </div>
                </div>

                <!-- Характеристики баллона (партия) -->
                <template v-if="editData.container_type === 'Баллон'">
                  <hr>
                  <h6 class="text-muted mb-3">Характеристики баллона</h6>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Модель:</label>
                    <div class="col-sm-9"><input type="text" class="form-control" v-model="editData.model" placeholder="YSP23.9/10"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Материал корпуса:</label>
                    <div class="col-sm-9"><input type="text" class="form-control" v-model="editData.material" placeholder="HP295"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Стандарт:</label>
                    <div class="col-sm-9"><input type="text" class="form-control" v-model="editData.standard" placeholder="GB 5842"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Рабочее давление, МПа:</label>
                    <div class="col-sm-9"><input type="number" step="0.01" class="form-control" v-model="editData.working_pressure_mpa" placeholder="2.1"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Испытательное давление, МПа:</label>
                    <div class="col-sm-9"><input type="number" step="0.01" class="form-control" v-model="editData.hydro_test_pressure_mpa" placeholder="3.2"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Давление герметичности, МПа:</label>
                    <div class="col-sm-9"><input type="number" step="0.01" class="form-control" v-model="editData.tightness_pressure_mpa" placeholder="2.1"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Наружный диаметр, мм:</label>
                    <div class="col-sm-9"><input type="number" step="0.1" class="form-control" v-model="editData.outer_diameter_mm" placeholder="280"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Толщина стенки, мм:</label>
                    <div class="col-sm-9"><input type="number" step="0.01" class="form-control" v-model="editData.wall_thickness_mm" placeholder="2.1"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Заводской № партии:</label>
                    <div class="col-sm-9"><input type="text" class="form-control" v-model="editData.factory_batch" placeholder="001262006"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Дата изготовления:</label>
                    <div class="col-sm-9"><input type="text" class="form-control" v-model="editData.manufacture_date" placeholder="2026-03"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Лицензия:</label>
                    <div class="col-sm-9"><input type="text" class="form-control" v-model="editData.license_number" placeholder="TS2237T23-2028"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Термообработка:</label>
                    <div class="col-sm-9"><input type="text" class="form-control" v-model="editData.heat_treatment"></div>
                  </div>

                  <hr>
                  <h6 class="text-muted mb-3">Гидроиспытание</h6>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Давление испытания, МПа:</label>
                    <div class="col-sm-9"><input type="number" step="0.01" class="form-control" v-model="editData.test_pressure_mpa" placeholder="3.22"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Давление испытания, атм:</label>
                    <div class="col-sm-9"><input type="number" step="0.01" class="form-control" v-model="editData.test_pressure_atm" placeholder="31.78"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Дата испытания:</label>
                    <div class="col-sm-9"><input type="date" class="form-control" v-model="editData.test_date"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Результат:</label>
                    <div class="col-sm-9"><input type="text" class="form-control" v-model="editData.test_result" placeholder="годен"></div>
                  </div>
                  <div class="row mb-2">
                    <label class="col-sm-3 col-form-label">Время выдержки:</label>
                    <div class="col-sm-9"><input type="text" class="form-control" v-model="editData.hold_time" placeholder="30 сек"></div>
                  </div>
                </template>

                <div class="text-end">
                  <button type="button" @click="editMode = false" class="btn btn-secondary me-2">
                    Отмена
                  </button>
                  <button type="submit" class="btn btn-success" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                    Сохранить
                  </button>
                </div>
              </form>

              <!-- Режим просмотра -->
              <div v-else>
                <div class="row mb-3">
                  <div class="col-sm-3">
                    <strong>Серийный номер:</strong>
                  </div>
                  <div class="col-sm-9">
                    <span class="text-muted" v-if="!passport.serial_number">Не указан</span>
                    <span v-else>{{ passport.serial_number }}</span>
                  </div>
                </div>

                <div class="row mb-3" v-if="passport.batch">
                  <div class="col-sm-3">
                    <strong>Партия:</strong>
                  </div>
                  <div class="col-sm-9">{{ passport.batch }}</div>
                </div>

                <div class="row mb-3">
                  <div class="col-sm-3">
                    <strong>Тип емкости:</strong>
                  </div>
                  <div class="col-sm-9">
                    <span class="text-muted" v-if="!passport.container_type">Не указан</span>
                    <span v-else>{{ passport.container_type }}</span>
                  </div>
                </div>

                <div class="row mb-3" v-if="passport.container_type === 'Моноблок'">
                  <div class="col-sm-3">
                    <strong>Баллонов в связке:</strong>
                  </div>
                  <div class="col-sm-9">
                    <span class="text-muted" v-if="!passport.bundle_size">Не указано</span>
                    <span v-else>{{ passport.bundle_size }}</span>
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-sm-3">
                    <strong>Объем:</strong>
                  </div>
                  <div class="col-sm-9">
                    <SpecValue :spec="spec('volume')" unit="л" empty="Не указан" />
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-sm-3">
                    <strong>Давление:</strong>
                  </div>
                  <div class="col-sm-9">
                    <SpecValue :spec="spec('pressure')" unit="атм" empty="Не указано" />
                  </div>
                </div>

                <div class="row mb-3" v-if="passport.container_type === 'КЦ'">
                  <div class="col-sm-3">
                    <strong>Наличие рамы:</strong>
                  </div>
                  <div class="col-sm-9">
                    <span class="text-muted" v-if="passport.has_frame === null || passport.has_frame === undefined">Не указано</span>
                    <span v-else>{{ (passport.has_frame === 1 || passport.has_frame === true) ? 'Есть' : 'Нет' }}</span>
                  </div>
                </div>

                <div class="row mb-3" v-if="passport.container_type === 'КЦ'">
                  <div class="col-sm-3">
                    <strong>Наличие навесного испарителя:</strong>
                  </div>
                  <div class="col-sm-9">
                    <span class="text-muted" v-if="passport.has_evaporator === null || passport.has_evaporator === undefined">Не указано</span>
                    <span v-else>{{ (passport.has_evaporator === 1 || passport.has_evaporator === true) ? 'Есть' : 'Нет' }}</span>
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-sm-3">
                    <strong>Производитель:</strong>
                  </div>
                  <div class="col-sm-9">
                    <SpecValue :spec="spec('manufacturer')" empty="Не указан" />
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-sm-3">
                    <strong>Газ:</strong>
                  </div>
                  <div class="col-sm-9">
                    <SpecValue :spec="spec('gas')" :format="gasLabel" empty="Не указан" />
                  </div>
                </div>

                <!-- Пустое описание строки не занимает: сказать ему нечего -->
                <div class="row mb-3" v-if="passport.description">
                  <div class="col-sm-3">
                    <strong>Описание:</strong>
                  </div>
                  <div class="col-sm-9">
                    <span style="white-space: pre-wrap">{{ passport.description }}</span>
                  </div>
                </div>

                <!-- Характеристики из сертификата: свои у ёмкости либо унаследованные от партии -->
                <template v-if="visibleSpecs.length">
                  <hr>
                  <h6 class="text-muted mb-3">Характеристики баллона</h6>
                  <div class="row mb-2" v-for="spec in visibleSpecs" :key="spec.key">
                    <div class="col-sm-3"><strong>{{ spec.label }}:</strong></div>
                    <div class="col-sm-9">
                      {{ spec.text }}
                      <span v-if="spec.source === 'batch'" class="badge bg-light text-muted border ms-2">из партии</span>
                    </div>
                  </div>
                </template>

                <!-- Гидроиспытание (индивидуально) -->
                <template v-if="hasHydroTest">
                  <hr>
                  <h6 class="text-muted mb-3">Гидроиспытание</h6>
                  <div class="row mb-2" v-if="passport.test_pressure_mpa"><div class="col-sm-3"><strong>Давление испытания:</strong></div><div class="col-sm-9">{{ num(passport.test_pressure_mpa) }}&nbsp;МПа<span v-if="passport.test_pressure_atm"> ({{ num(passport.test_pressure_atm) }}&nbsp;атм)</span></div></div>
                  <div class="row mb-2" v-if="passport.test_date"><div class="col-sm-3"><strong>Дата испытания:</strong></div><div class="col-sm-9">{{ formatTestDate(passport.test_date) }}</div></div>
                  <div class="row mb-2" v-if="passport.test_result"><div class="col-sm-3"><strong>Результат:</strong></div><div class="col-sm-9">{{ passport.test_result }}</div></div>
                  <div class="row mb-2" v-if="passport.hold_time"><div class="col-sm-3"><strong>Время выдержки:</strong></div><div class="col-sm-9">{{ passport.hold_time }}</div></div>
                </template>
              </div>

              <!-- PDF файл паспорта: без файла строку не показываем вовсе -->
              <template v-if="passport.passport_file">
              <hr>
              <div class="row mb-3">
                <div class="col-sm-3">
                  <strong>PDF паспорт:</strong>
                </div>
                <div class="col-sm-9">
                  <div>
                    <a 
                      :href="`/uploads/${passport.passport_file}`" 
                      target="_blank"
                      class="btn btn-outline-primary btn-sm me-2"
                    >
                      <i class="bi bi-file-pdf"></i> Открыть PDF
                    </a>
                    <a 
                      :href="`/uploads/${passport.passport_file}`" 
                      :download="passport.passport_file"
                      class="btn btn-outline-success btn-sm me-2"
                    >
                      <i class="bi bi-download"></i> Скачать
                    </a>
                    <button 
                      v-if="isAdmin"
                      @click="deletePassportFile"
                      class="btn btn-outline-danger btn-sm"
                      :disabled="deletingFile"
                    >
                      <i class="bi bi-trash"></i> Удалить
                    </button>
                  </div>
                </div>
              </div>
              </template>

              <!-- Загрузка PDF файла (только для админов в режиме редактирования) -->
              <div v-if="isAdmin && editMode" class="row mb-3">
                <div class="col-sm-3">
                  <strong>Загрузить PDF:</strong>
                </div>
                <div class="col-sm-9">
                  <div class="input-group">
                    <input 
                      type="file" 
                      class="form-control" 
                      id="passportFileInput"
                      accept=".pdf,application/pdf"
                      @change="handleFileSelect"
                      ref="fileInput"
                    >
                    <button 
                      @click="uploadPassportFile"
                      class="btn btn-primary"
                      type="button"
                      :disabled="!selectedFile || uploadingFile"
                    >
                      <span v-if="uploadingFile" class="spinner-border spinner-border-sm me-2"></span>
                      <i v-else class="bi bi-upload"></i>
                      {{ uploadingFile ? 'Загрузка...' : 'Загрузить' }}
                    </button>
                  </div>
                  <div v-if="selectedFile" class="form-text">
                    Выбран файл: {{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})
                  </div>
                </div>
              </div>

              <!-- Дата создания и обновления -->
              <hr>
              <div class="row">
                <div class="col-sm-6">
                  <small class="text-muted">
                    <strong>Создан:</strong> {{ formatDate(passport.created_at) }}
                  </small>
                </div>
                <div class="col-sm-6 text-sm-end">
                  <small class="text-muted">
                    <strong>Обновлен:</strong> {{ formatDate(passport.updated_at) }}
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- Кнопки навигации -->
          <div class="text-center mt-4">
            <router-link to="/" class="btn btn-outline-primary">
              <i class="bi bi-house"></i>
              На главную
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Уведомления -->
    <div v-if="successMessage" class="position-fixed top-0 end-0 p-3" style="z-index: 1050">
      <div class="toast show" role="alert">
        <div class="toast-header bg-success text-white">
          <strong class="me-auto">Успех</strong>
          <button type="button" class="btn-close btn-close-white" @click="successMessage = ''"></button>
        </div>
        <div class="toast-body">
          {{ successMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isAdmin } from '@/auth'
import { isNaucheteHost } from '@/qrDomains'
import axios from 'axios'
import SpecValue from '../components/SpecValue.vue'

const GAS_LABELS = {
  Ar: 'Аргон (Ar)', O2: 'Кислород (O2)', N2: 'Азот (N2)', CO2: 'Углекислота (CO2)',
  He: 'Гелий (He)', C3H8: 'Пропан (C3H8)', LPG: 'Пропан-бутан (LPG)'
}

// Подписи характеристик. Значения и их источник приходят с сервера в specs.
const SPEC_LABELS = [
  { key: 'model', label: 'Модель' },
  { key: 'material', label: 'Материал корпуса' },
  { key: 'standard', label: 'Стандарт' },
  { key: 'working_pressure_mpa', label: 'Рабочее давление', unit: 'МПа' },
  { key: 'hydro_test_pressure_mpa', label: 'Испытательное давление', unit: 'МПа' },
  { key: 'tightness_pressure_mpa', label: 'Давление герметичности', unit: 'МПа' },
  { key: 'outer_diameter_mm', label: 'Наружный диаметр', unit: 'мм' },
  { key: 'wall_thickness_mm', label: 'Толщина стенки', unit: 'мм' },
  { key: 'factory_batch', label: 'Заводской № партии' },
  { key: 'manufacture_date', label: 'Дата изготовления' },
  { key: 'license_number', label: 'Лицензия' },
  { key: 'heat_treatment', label: 'Термообработка' }
]

export default {
  name: 'PassportView',
  components: { SpecValue },
  data() {
    return {
      nauchete: isNaucheteHost(),
      passport: null,
      loading: true,
      notFound: false,
      invalidCode: false,
      ambiguous: [],
      lookalikes: [],
      movedFrom: null,
      batch: null,
      batches: [],
      descriptionOpen: false,
      specs: {},
      selectedBatchId: null,
      savingBatch: false,
      knownPrefixes: [],
      reassignOpen: false,
      reassignPrefix: '',
      reassignIdentifier: '',
      reassigning: false,
      reassignError: '',
      error: null,
      editMode: false,
      saving: false,
      successMessage: '',
      selectedFile: null,
      uploadingFile: false,
      deletingFile: false,
      editData: {
        serial_number: '',
        batch: '',
        container_type: null,
        bundle_size: null,
        volume: 0,
        pressure: 0,
        has_frame: false,
        has_evaporator: false,
        manufacturer: '',
        gas: null,
        description: '',
        model: '',
        material: '',
        standard: '',
        working_pressure_mpa: '',
        hydro_test_pressure_mpa: '',
        tightness_pressure_mpa: '',
        outer_diameter_mm: '',
        wall_thickness_mm: '',
        factory_batch: '',
        manufacture_date: '',
        license_number: '',
        heat_treatment: '',
        test_date: '',
        test_pressure_mpa: '',
        test_pressure_atm: '',
        test_result: '',
        hold_time: ''
      }
    }
  },
  computed: {
    isAdmin() {
      return isAdmin()
    },
    // Сервер уже решил, что берётся у ёмкости, а что унаследовано от партии
    visibleSpecs() {
      return SPEC_LABELS
        .map((field) => ({ ...field, ...(this.specs[field.key] || {}) }))
        .filter((field) => field.value !== null && field.value !== undefined && `${field.value}` !== '')
        .map((field) => ({ ...field, text: this.formatSpec(field) }))
    },

    hasCylinderSpecs() {
      const p = this.passport || {}
      return !!(p.model || p.material || p.standard || p.working_pressure_mpa || p.hydro_test_pressure_mpa ||
        p.tightness_pressure_mpa || p.outer_diameter_mm || p.wall_thickness_mm || p.factory_batch ||
        p.manufacture_date || p.license_number || p.heat_treatment)
    },
    hasHydroTest() {
      const p = this.passport || {}
      return !!(p.test_pressure_mpa || p.test_date || p.test_result || p.hold_time)
    }
  },
  mounted() {
    this.loadPassport()
    if (this.isAdmin) {
      this.loadKnownPrefixes()
      this.loadBatches()
    }
    // Проверяем query параметр для автоматического открытия режима редактирования
    if (this.$route.query.edit === 'true' && this.isAdmin) {
      this.editMode = true
    }
  },
  watch: {
    '$route'(to, from) {
      this.loadPassport()
      // Проверяем query параметр при изменении маршрута
      if (to.query.edit === 'true' && this.isAdmin) {
        this.editMode = true
      } else {
        this.editMode = false
      }
    }
  },
  methods: {
    async loadPassport() {
      this.loading = true
      this.notFound = false
      this.error = null
      this.invalidCode = false
      this.ambiguous = []
      this.lookalikes = []
      this.movedFrom = null
      this.batch = null
      this.specs = {}
      this.descriptionOpen = false
      this.reassignOpen = false
      this.reassignError = ''

      try {
        const identifier = this.$route.params.identifier
        const response = await axios.get(`/api/passport/${encodeURIComponent(identifier)}`)
        
        if (response.data.success) {
          // Приводим адрес к каноническому: один паспорт — одна ссылка
          const canonical = response.data.canonical
          if (canonical && canonical !== identifier) {
            this.$router.replace(`/${canonical}`)
            return
          }
          this.lookalikes = response.data.lookalikes || []
          this.movedFrom = response.data.movedFrom || null
          this.batch = response.data.batch || null
          this.specs = response.data.specs || {}
          this.selectedBatchId = (response.data.data && response.data.data.batch_id) || null
          this.passport = response.data.data
          this.editData = {
            serial_number: this.passport.serial_number || '',
            batch: this.passport.batch || '',
            container_type: this.passport.container_type || null,
            bundle_size: this.passport.bundle_size || null,
            volume: this.passport.volume || 0,
            pressure: this.passport.pressure || 0,
            has_frame: this.passport.has_frame === 1 || this.passport.has_frame === true || false,
            has_evaporator: this.passport.has_evaporator === 1 || this.passport.has_evaporator === true || false,
            manufacturer: this.passport.manufacturer || '',
            gas: this.passport.gas || null,
            description: this.passport.description || '',
            model: this.passport.model || '',
            material: this.passport.material || '',
            standard: this.passport.standard || '',
            working_pressure_mpa: this.passport.working_pressure_mpa ?? '',
            hydro_test_pressure_mpa: this.passport.hydro_test_pressure_mpa ?? '',
            tightness_pressure_mpa: this.passport.tightness_pressure_mpa ?? '',
            outer_diameter_mm: this.passport.outer_diameter_mm ?? '',
            wall_thickness_mm: this.passport.wall_thickness_mm ?? '',
            factory_batch: this.passport.factory_batch || '',
            manufacture_date: this.passport.manufacture_date || '',
            license_number: this.passport.license_number || '',
            heat_treatment: this.passport.heat_treatment || '',
            test_date: this.passport.test_date ? String(this.passport.test_date).slice(0, 10) : '',
            test_pressure_mpa: this.passport.test_pressure_mpa ?? '',
            test_pressure_atm: this.passport.test_pressure_atm ?? '',
            test_result: this.passport.test_result || '',
            hold_time: this.passport.hold_time || ''
          }
          
          // Автоматически открываем режим редактирования, если есть query параметр и пользователь админ
          if (this.$route.query.edit === 'true' && this.isAdmin) {
            this.editMode = true
          }
        }
      } catch (error) {
        const status = error.response && error.response.status
        const payload = (error.response && error.response.data) || {}

        if (status === 409 && payload.ambiguous) {
          this.ambiguous = payload.data || []
        } else if (status === 400 && payload.invalid) {
          this.invalidCode = true
        } else if (status === 404) {
          this.notFound = true
        } else {
          this.error = 'Произошла ошибка при загрузке паспорта'
          console.error('Ошибка загрузки паспорта:', error)
        }
      } finally {
        this.loading = false
      }
    },

    // Числа приходят из DECIMAL строкой вида «2.10» — убираем хвостовые нули,
    // разделитель оставляем точкой, единицу цепляем неразрывным пробелом
    // Значение поля с источником: своё или партийное
    spec(key) {
      return this.specs[key] || {}
    },

    gasLabel(value) {
      return GAS_LABELS[value] || `${value}`
    },

    // Хвостовые нули из DECIMAL: «23.90» → «23.9», «280.0» → «280»
    num(value) {
      const number = Number(value)
      return Number.isFinite(number) ? `${number}` : `${value}`
    },

    formatSpec(field) {
      if (!field.unit) return `${field.value}`
      const number = Number(field.value)
      const text = Number.isFinite(number) ? `${number}` : `${field.value}`
      return `${text}\u00A0${field.unit}`
    },

    async loadBatches() {
      try {
        const response = await axios.get('/api/batches')
        this.batches = response.data.data || []
      } catch (error) {
        this.batches = []
      }
    },

    async saveBatch() {
      this.savingBatch = true
      try {
        const target = this.selectedBatchId || (this.passport.batch_id || null)
        if (!target) {
          this.savingBatch = false
          return
        }

        await axios.post(
          `/api/batches/${target}/passports`,
          { identifiers: [this.passport.identifier], detach: !this.selectedBatchId },
          { headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') } }
        )
        await this.loadPassport()
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.message)
          || 'Не удалось привязать партию'
      } finally {
        this.savingBatch = false
      }
    },

    async loadKnownPrefixes() {
      try {
        const response = await axios.get('/api/prefixes')
        this.knownPrefixes = response.data.data || []
      } catch (error) {
        this.knownPrefixes = []
      }
    },

    async reassignIdentifierAction() {
      this.reassigning = true
      this.reassignError = ''

      try {
        const response = await axios.post(
          `/api/passport/${encodeURIComponent(this.passport.identifier)}/reassign`,
          {
            prefix: this.reassignIdentifier.trim() ? '' : this.reassignPrefix.trim(),
            identifier: this.reassignIdentifier.trim()
          },
          { headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') } }
        )

        if (response.data.success) {
          this.reassignOpen = false
          this.reassignPrefix = ''
          this.reassignIdentifier = ''
          this.$router.replace(`/${response.data.data.identifier}`)
        }
      } catch (error) {
        this.reassignError = (error.response && error.response.data && error.response.data.message)
          || 'Не удалось сменить идентификатор'
      } finally {
        this.reassigning = false
      }
    },

    async savePassport() {
      this.saving = true

      try {
        const dataToSend = {
          ...this.editData,
          has_frame: this.editData.container_type === 'КЦ' ? this.editData.has_frame : null,
          has_evaporator: this.editData.container_type === 'КЦ' ? this.editData.has_evaporator : null
        }
        
        const response = await axios.put(`/api/passport/${this.passport.identifier}`, dataToSend, {
          headers: { 'X-Admin-Auth': localStorage.getItem('adminAuth') }
        })
        
        if (response.data.success) {
          // Обновляем данные паспорта
          Object.assign(this.passport, dataToSend)
          this.passport.updated_at = new Date().toISOString()
          
          this.editMode = false
          this.successMessage = 'Паспорт успешно обновлен'
          
          // Скрываем уведомление через 3 секунды
          setTimeout(() => {
            this.successMessage = ''
          }, 3000)
        }
      } catch (error) {
        this.error = 'Ошибка при сохранении паспорта'
        console.error('Ошибка сохранения:', error)
      } finally {
        this.saving = false
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleString('ru-RU')
    },

    formatTestDate(value) {
      if (!value) return ''
      const d = new Date(value)
      return isNaN(d.getTime()) ? value : d.toLocaleDateString('ru-RU')
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        if (file.type !== 'application/pdf') {
          alert('Пожалуйста, выберите PDF файл')
          this.$refs.fileInput.value = ''
          return
        }
        if (file.size > 10 * 1024 * 1024) {
          alert('Размер файла не должен превышать 10 МБ')
          this.$refs.fileInput.value = ''
          return
        }
        this.selectedFile = file
      }
    },

    async uploadPassportFile() {
      if (!this.selectedFile) {
        return
      }

      this.uploadingFile = true
      this.error = null

      try {
        const formData = new FormData()
        formData.append('passportFile', this.selectedFile)

        const response = await axios.post(
          `/api/passport/${this.passport.identifier}/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-Admin-Auth': localStorage.getItem('adminAuth')
            }
          }
        )

        if (response.data.success) {
          // Обновляем данные паспорта
          this.passport.passport_file = response.data.data.fileName
          this.selectedFile = null
          this.$refs.fileInput.value = ''
          this.successMessage = 'PDF файл успешно загружен'
          
          setTimeout(() => {
            this.successMessage = ''
          }, 3000)
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Ошибка при загрузке файла'
        console.error('Ошибка загрузки файла:', error)
      } finally {
        this.uploadingFile = false
      }
    },

    async deletePassportFile() {
      if (!confirm('Вы уверены, что хотите удалить прикрепленный PDF файл?')) {
        return
      }

      this.deletingFile = true

      try {
        const response = await axios.delete(
          `/api/passport/${this.passport.identifier}/file`,
          {
            headers: {
              'X-Admin-Auth': localStorage.getItem('adminAuth')
            }
          }
        )

        if (response.data.success) {
          this.passport.passport_file = null
          this.successMessage = 'PDF файл успешно удален'
          
          setTimeout(() => {
            this.successMessage = ''
          }, 3000)
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Ошибка при удалении файла'
        console.error('Ошибка удаления файла:', error)
      } finally {
        this.deletingFile = false
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
/* Без min-width: 0 flex-элемент не сжимается ниже своего содержимого,
   и широкая таблица распирает всю страницу на телефоне */
.batch-description-col {
  min-width: 0;
}

.batch-description {
  background: #f8f9fa;
  min-width: 0;
}

/* Таблицы из сертификата шире экрана — химсостав это десять колонок.
   display:block + max-content даёт таблице свою ширину, а max-width и
   overflow-x прячут лишнее в собственную прокрутку, не растягивая страницу. */
.batch-description :deep(table) {
  display: block;
  width: max-content;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin-bottom: 0.5rem;
  font-size: 0.9375rem;
}

.batch-description :deep(td),
.batch-description :deep(th) {
  border: 1px solid #dee2e6;
  padding: 0.25rem 0.5rem;
}

.batch-description :deep(h4) {
  font-size: 1.125rem;
  margin-top: 1rem;
}

.batch-description :deep(p:last-child) {
  margin-bottom: 0;
}

.passport-container {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.card {
  border: none;
  border-radius: 15px;
}

.card-header {
  border-radius: 15px 15px 0 0 !important;
}

.registry-passport {
  --registry-teal: #2699b1;
  --registry-teal-dark: #168ba4;
  --registry-navy: #17344c;
  --registry-border: #d3e5ed;
  color: var(--registry-navy);
}

.registry-passport .card.shadow-lg {
  overflow: hidden;
  border: 1px solid var(--registry-border);
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(23, 75, 101, .12) !important;
}

.registry-passport .card-header.bg-primary {
  padding: 18px 24px;
  border: 0;
  border-radius: 11px 11px 0 0 !important;
  background: linear-gradient(120deg, var(--registry-teal-dark), var(--registry-teal)) !important;
}

.registry-passport .card-header h3 {
  font-weight: 750;
  letter-spacing: -.02em;
}

.registry-passport .badge.bg-primary {
  border-radius: 7px;
  background: var(--registry-teal) !important;
  letter-spacing: .025em;
}

.registry-passport .btn {
  border-radius: 7px;
}

.registry-passport .btn-primary {
  border-color: var(--registry-teal);
  background: var(--registry-teal);
}

.registry-passport .btn-primary:hover,
.registry-passport .btn-primary:focus {
  border-color: var(--registry-teal-dark);
  background: var(--registry-teal-dark);
}

.registry-passport .btn-outline-primary {
  border-color: var(--registry-teal);
  color: var(--registry-teal-dark);
}

.registry-passport .btn-outline-primary:hover,
.registry-passport .btn-outline-primary:focus {
  border-color: var(--registry-teal);
  background: var(--registry-teal);
  color: #fff;
}

.badge {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}

.toast {
  min-width: 300px;
}

.bi-file-text::before { content: "📄"; }
.bi-pencil::before { content: "✏️"; }
.bi-house::before { content: "🏠"; }
.bi-exclamation-triangle::before { content: "⚠️"; }
.bi-x-circle::before { content: "❌"; }
.bi-file-pdf::before { content: "📕"; }
.bi-download::before { content: "⬇️"; }
.bi-upload::before { content: "⬆️"; }
.bi-trash::before { content: "🗑️"; }
</style>
